/**
 * EmployeesListPage
 * Main page for listing and managing employees
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Add,
  DeleteSweep,
  FileDownload,
  FileUpload,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useEmployees, useEmployeeExport } from '../../hooks';
import {
  EmployeeTable,
  EmployeeSearchBar,
  EmployeesWithoutCompanyBlock
} from '../../components/employees';
import { PageShell } from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { ActionButton, RouteActionButton } from '../../components/common';
import { Employee } from '../../types/employee';
import EmployeeImportDialog from './components/EmployeeImportDialog';

export const EmployeesListPage: React.FC = () => {
  const navigate = useNavigate();
  const agendar = useControle('/appointments/new', pode('appointment.create'), 'Agendar Consulta');
  const visualizar = useControle('/employees/:id', pode('employee.read'), 'Visualizar');
  const editar = useControle('/employees/:id/edit', pode('employee.update'), 'Editar');
  const excluir = useAcao('employee.delete', 'Excluir');

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Hooks
  const {
    employees,
    loading,
    pagination,
    filters,
    sortBy,
    sortOrder,
    setPage,
    setLimit,
    setFilters,
    setSorting,
    refresh,
    deleteEmployee
  } = useEmployees();

  const { exporting, exportEmployees } = useEmployeeExport();

  // Filtro sem resultado não é ausência de dado: só conta como filtro campo preenchido,
  // nunca paginação ou ordenação.
  const filtersActive = Object.entries(filters).some(
    ([key, value]) =>
      !['page', 'limit', 'sortBy', 'sortOrder'].includes(key) &&
      value !== undefined &&
      value !== null &&
      value !== ''
  );

  // Handlers
  const handleView = (employee: Employee) => {
    navigate(`/employees/${employee.id}`);
  };

  const handleEdit = (employee: Employee) => {
    navigate(`/employees/${employee.id}/edit`);
  };

  const handleScheduleAppointment = (employee: Employee) => {
    navigate(`/appointments/new?employeeId=${employee.id}`);
  };

  const handleDeleteClick = (employee: Employee) => {
    setEmployeeToDelete(employee);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (employeeToDelete) {
      const success = await deleteEmployee(employeeToDelete.id);
      if (success) {
        setDeleteDialogOpen(false);
        setEmployeeToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setEmployeeToDelete(null);
  };

  const handleBulkDelete = async () => {
    // TODO: Implement bulk delete
    console.log('Bulk delete:', selectedIds);
  };

  const handleExport = async () => {
    await exportEmployees(filters, `funcionarios-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleImportClick = () => {
    setImportDialogOpen(true);
  };

  const handleImportDialogClose = () => {
    setImportDialogOpen(false);
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Funcionários' },
      ]}
      title="Funcionários"
      actions={
        <>
          <Tooltip title="Exportar funcionários">
            <span>
              <Button
                variant="outlined"
                startIcon={<FileDownload />}
                onClick={handleExport}
                disabled={exporting}
              >
                {exporting ? 'Exportando...' : 'Exportar'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Importar funcionários">
            <Button variant="outlined" startIcon={<FileUpload />} onClick={handleImportClick}>
              Importar
            </Button>
          </Tooltip>
          <RouteActionButton
            para="/employees/new"
            exige={pode('employee.create')}
            variant="contained"
            startIcon={<Add />}
          >
            Novo Funcionário
          </RouteActionButton>
        </>
      }
    >
        {/* Search Bar with Filters */}
        <Box mb={3}>
          <EmployeeSearchBar
            filters={filters}
            onFiltersChange={setFilters}
            exporting={exporting}
            total={pagination.total}
          />
        </Box>

        {/* Selection Toolbar - Only visible when items are selected */}
        {selectedIds.length > 0 && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {selectedIds.length} funcionário(s) selecionado(s)
              </Typography>
<ActionButton
                acao="employee.delete"
                rotulo="Excluir Selecionados"
                variant="outlined"
                color="error"
                startIcon={<DeleteSweep />}
                onClick={handleBulkDelete}
              >
                Excluir Selecionados
              </ActionButton>
            </Box>
          </Paper>
        )}

        <EmployeesWithoutCompanyBlock />

        {/* Employee Table */}
        <EmployeeTable
          employees={employees}
          loading={loading}
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onPageChange={setPage}
          onLimitChange={setLimit}
          onSortChange={setSorting}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onScheduleAppointment={handleScheduleAppointment}
          controleAgendar={agendar}
          controleVer={visualizar}
          controleEditar={editar}
          controleExcluir={excluir}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          filtersActive={filtersActive}
          onClearFilters={() => setFilters({})}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
          <DialogTitle>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja excluir o funcionário{' '}
              <strong>{employeeToDelete?.name}</strong>?
              <br />
              Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel}>Cancelar</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Excluir
            </Button>
          </DialogActions>
        </Dialog>

        {/* Import Dialog */}
        <EmployeeImportDialog
          open={importDialogOpen}
          onClose={handleImportDialogClose}
          onImportComplete={refresh}
        />
    </PageShell>
  );
};
